import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Bot, Sparkles, Target, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ProcessingScreen } from "@/components/ProcessingScreen";
import { api, waitForStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const GEN_STEPS = [
  "Costruzione del gemello digitale dell'azienda…",
  "Simulazione scenario fotovoltaico e accumulo…",
  "Valutazione PPA / SEU e comunità energetica…",
  "Analisi rifasamento, relamping LED, pompe di calore…",
  "Calcolo ROI e tempi di rientro…",
  "Verifica incentivi disponibili…",
  "Composizione dell'Energy Opportunity Index…",
];

const HORIZONS = [
  { v: "immediato", l: "Immediato", d: "Entro 30 giorni" },
  { v: "3_mesi", l: "Entro 3 mesi", d: "Nel breve termine" },
  { v: "6_mesi", l: "Entro 6 mesi", d: "Pianificazione a breve" },
  { v: "12_mesi", l: "Entro 12 mesi", d: "Nel prossimo anno" },
  { v: "esplorativo", l: "Solo esplorativo", d: "Sto valutando le opzioni" },
];
const PRIORITIES = [
  { v: "costi", l: "Abbattimento costi fissi", d: "Ridurre subito la bolletta" },
  { v: "indipendenza", l: "Indipendenza energetica", d: "Autoprodurre e non dipendere dalla rete" },
  { v: "sostenibilita", l: "Sostenibilità / ESG", d: "Ridurre le emissioni e migliorare l'immagine" },
  { v: "protezione", l: "Protezione dai rincari", d: "Difendersi dalla volatilità dei prezzi" },
  { v: "iperammortamento", l: "Iperammortamento / incentivi", d: "Sfruttare le agevolazioni fiscali" },
];
const BUDGETS = [
  { v: "<50k", l: "< 50.000 €" }, { v: "50-150k", l: "50–150.000 €" },
  { v: "150-300k", l: "150–300.000 €" }, { v: ">300k", l: "> 300.000 €" },
  { v: "da_valutare", l: "Da valutare" },
];

export default function Audit() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [phase, setPhase] = useState("questions");
  const [horizon, setHorizon] = useState("");
  const [priorities, setPriorities] = useState([]);
  const [budget, setBudget] = useState("");
  const [company, setCompany] = useState(null);
  const [cliIndirizzo, setCliIndirizzo] = useState("");
  const [cliCap, setCliCap] = useState("");

  useEffect(() => {
    api.get(`/leads/${leadId}/questions`)
      .then((r) => setQuestions(r.data.questions || []))
      .catch(() => toast.error("Impossibile caricare le domande"));
    api.get(`/leads/${leadId}`)
      .then((r) => {
        const c = r.data?.company || {};
        setCompany(c);
        if (c.indirizzo) setCliIndirizzo(c.indirizzo);
        if (c.cap) setCliCap(c.cap);
      })
      .catch(() => { /* non blocca l'audit */ });
  }, [leadId]);

  if (submitting) {
    return <ProcessingScreen steps={GEN_STEPS} done={done}
      title="L'AI sta creando il gemello digitale della tua azienda"
      subtitle="Simulazione degli scenari di intervento ed elaborazione del dossier." />;
  }

  if (!questions) return <div className="min-h-screen flex items-center justify-center text-slate-500">Preparazione dell'intervista…</div>;

  if (phase === "anagrafica") {
    const cittaProv = [company?.citta, company?.provincia].filter(Boolean).join(" · ");
    return (
      <div className="min-h-screen flex flex-col" data-testid="client-details-step">
        <header className="glass">
          <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
            <Logo />
            <span className="text-xs text-gold-400 font-mono-plex">Dati per il preventivo</span>
          </div>
          <div className="h-0.5 bg-white/5"><div className="h-full bg-gold-500" style={{ width: "100%" }} /></div>
        </header>
        <main className="flex-1 flex items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="max-w-2xl w-full mx-auto px-6 py-12">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold-500/10 border border-gold-500/30">
                <MapPin className="h-4 w-4 text-gold-400" />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Anagrafica per il preventivo</span>
            </div>
            <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tight text-white mb-2">Sede della tua azienda</h2>
            <p className="text-slate-400 mb-8 max-w-2xl">
              Servono per intestare correttamente il preventivo. {cittaProv && (<>Città e provincia rilevate: <span className="text-white font-medium">{cittaProv}</span>.</>)} Puoi anche saltare e fornirli in seguito.
            </p>

            <div className="space-y-5">
              <div>
                <label className="text-slate-300 text-sm mb-2 block">Indirizzo (via e numero civico)</label>
                <Input data-testid="input-cliente-indirizzo" value={cliIndirizzo}
                  onChange={(e) => setCliIndirizzo(e.target.value)}
                  placeholder="Es. Via Terracini 44" className="input-dark h-12 rounded-xl" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-slate-300 text-sm mb-2 block">CAP</label>
                  <Input data-testid="input-cliente-cap" value={cliCap}
                    onChange={(e) => setCliCap(e.target.value)} inputMode="numeric" maxLength={5}
                    placeholder="Es. 51031" className="input-dark h-12 rounded-xl" />
                </div>
                <div className="col-span-2">
                  <label className="text-slate-300 text-sm mb-2 block">Città e provincia</label>
                  <div className="h-12 rounded-xl border border-white/10 bg-white/[0.03] px-4 flex items-center text-slate-300 text-sm" data-testid="cliente-citta-prov">
                    {cittaProv || "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-10">
              <button onClick={() => saveClientDetails(true)} data-testid="client-details-skip-btn"
                className="text-sm text-slate-400 hover:text-white transition-colors">Salta questo passaggio</button>
              <Button onClick={() => saveClientDetails(false)} data-testid="client-details-continue-btn"
                className="h-12 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold group">
                Continua
                <ArrowRight className="h-4.5 w-4.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  if (phase === "priority") {
    return (
      <div className="min-h-screen flex flex-col" data-testid="priority-map-step">
        <header className="glass">
          <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
            <Logo />
            <span className="text-xs text-gold-400 font-mono-plex">Mappatura priorità</span>
          </div>
          <div className="h-0.5 bg-white/5"><div className="h-full bg-gold-500" style={{ width: "100%" }} /></div>
        </header>
        <main className="flex-1">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="max-w-4xl w-full mx-auto px-6 py-12">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold-500/10 border border-gold-500/30">
                <Target className="h-4 w-4 text-gold-400" />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-amber-500">Ultimo passaggio · consigliato</span>
            </div>
            <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tight text-white mb-2">Le tue priorità</h2>
            <p className="text-slate-400 mb-8 max-w-2xl">Aiutaci a personalizzare la proposta: indica i tempi e gli obiettivi della tua azienda. Chi vuole intervenire prima riceve priorità nella nostra pianificazione.</p>

            <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300 mb-3">Orizzonte temporale dell'investimento</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
              {HORIZONS.map((h) => (
                <button key={h.v} data-testid={`horizon-${h.v}`} onClick={() => setHorizon(h.v)}
                  className={`text-left p-4 rounded-2xl border transition-all duration-300 ${horizon === h.v ? "border-amber-500 bg-amber-500/10 shadow-[0_0_20px_rgba(245,158,11,0.15)]" : "border-white/10 bg-white/[0.02] hover:border-gold-500/40"}`}>
                  <div className="text-white font-semibold">{h.l}</div>
                  <div className="text-xs text-slate-400 mt-1">{h.d}</div>
                </button>
              ))}
            </div>

            <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300 mb-1">Priorità aziendale</h3>
            <p className="text-xs text-slate-500 mb-3">Seleziona fino a 2 obiettivi principali</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {PRIORITIES.map((p) => (
                <button key={p.v} data-testid={`priority-${p.v}`} onClick={() => togglePriority(p.v)}
                  className={`flex items-start justify-between text-left p-4 rounded-2xl border transition-all duration-300 ${priorities.includes(p.v) ? "border-amber-500 bg-amber-500/10 shadow-[0_0_20px_rgba(245,158,11,0.15)]" : "border-white/10 bg-white/[0.02] hover:border-gold-500/40"}`}>
                  <div>
                    <div className="text-white font-semibold">{p.l}</div>
                    <div className="text-xs text-slate-400 mt-1">{p.d}</div>
                  </div>
                  {priorities.includes(p.v) && <Check className="h-4.5 w-4.5 text-gold-400 shrink-0 ml-2" />}
                </button>
              ))}
            </div>

            <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300 mb-3">Budget indicativo <span className="text-slate-500 normal-case tracking-normal">(facoltativo)</span></h3>
            <div className="flex flex-wrap gap-2 mb-12">
              {BUDGETS.map((b) => (
                <button key={b.v} data-testid={`budget-${b.v}`} onClick={() => setBudget(budget === b.v ? "" : b.v)}
                  className={`px-4 h-10 rounded-full border text-sm transition-all duration-300 ${budget === b.v ? "border-amber-500 bg-amber-500/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-gold-500/40"}`}>
                  {b.l}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <button onClick={() => completePriority(true)} data-testid="priority-skip-btn"
                className="text-sm text-slate-400 hover:text-white transition-colors">Salta questo passaggio</button>
              <Button onClick={() => completePriority(false)} data-testid="priority-complete-btn"
                className="h-12 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold group">
                Genera il mio dossier
                <ArrowRight className="h-4.5 w-4.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  const q = questions[idx];
  const total = questions.length;
  const progress = Math.round(((idx) / total) * 100);

  const saveCurrent = () => {
    setAnswers((a) => ({ ...a, [q.id]: { id: q.id, question: q.question, answer: current } }));
  };

  const next = () => {
    if (!current.trim()) {
      toast.error("Inserisci una risposta"); return;
    }
    const updated = { ...answers, [q.id]: { id: q.id, question: q.question, answer: current } };
    setAnswers(updated);
    if (idx < total - 1) {
      const nextQ = questions[idx + 1];
      setIdx(idx + 1);
      setCurrent(updated[nextQ.id]?.answer || "");
    } else {
      setPhase("anagrafica");
    }
  };

  function togglePriority(v) {
    setPriorities((p) => {
      if (p.includes(v)) return p.filter((x) => x !== v);
      if (p.length >= 2) { toast.info("Puoi selezionare al massimo 2 priorità"); return p; }
      return [...p, v];
    });
  };

  async function saveClientDetails(skip) {
    if (!skip) {
      try {
        await api.post(`/leads/${leadId}/client-details`, {
          indirizzo: cliIndirizzo, cap: cliCap,
          citta: company?.citta || "", provincia: company?.provincia || "",
        });
      } catch { /* non blocca il completamento dell'audit */ }
    }
    setPhase("priority");
  }

  async function completePriority(skip) {
    if (!skip) {
      try {
        await api.post(`/leads/${leadId}/priority-map`, { horizon, priorities, budget });
      } catch { /* non blocca il completamento dell'audit */ }
    }
    finish(answers);
  };

  const prev = () => {
    if (idx === 0) return;
    saveCurrent();
    const prevQ = questions[idx - 1];
    setIdx(idx - 1);
    setCurrent(answers[prevQ.id]?.answer || "");
  };

  // Risposta rapida: precompila il campo di testo (l'utente può poi modificare/aggiungere note liberamente)
  const pick = (val) => setCurrent(val);

  async function finish(allAnswers) {
    setSubmitting(true);
    setDone(false);
    try {
      await api.post(`/leads/${leadId}/audit`, { answers: Object.values(allAnswers) });
      const final = await waitForStatus(leadId, ["completed", "audit_error"]);
      if (final === "audit_error" || final === "timeout") {
        setSubmitting(false);
        toast.error("Errore durante l'elaborazione. Riprova.");
        return;
      }
      setDone(true);
      setTimeout(() => navigate(`/report/${leadId}`), 1400);
    } catch {
      setSubmitting(false);
      toast.error("Errore durante l'elaborazione. Riprova.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <span className="text-xs text-slate-400 font-mono-plex">{idx + 1} / {total}</span>
        </div>
        <div className="h-0.5 bg-white/5">
          <motion.div className="h-full bg-gold-500" animate={{ width: `${progress}%` }} />
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="max-w-2xl w-full mx-auto px-6 py-16">
          <AnimatePresence mode="wait">
            <motion.div key={idx}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}>
              <div className="flex items-center gap-2.5 mb-5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold-500/10 border border-gold-500/30">
                  <Bot className="h-4 w-4 text-gold-400" />
                </div>
                <span className="text-xs uppercase tracking-widest text-slate-500">Audit guidato · Domanda {idx + 1}</span>
              </div>

              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white leading-tight mb-3">
                {q.question}
              </h2>

              {q.hint && (
                <p data-testid="question-hint" className="flex items-start gap-2 text-sm text-gold-300/80 mb-6">
                  <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-gold-400" />
                  <span>{q.hint}</span>
                </p>
              )}

              {(q.type === "yes_no") && (
                <div className="grid grid-cols-2 gap-4 mb-4" data-testid="answer-yesno">
                  {["Sì", "No"].map((opt) => (
                    <button key={opt} data-testid={`answer-${opt}`} onClick={() => pick(opt)}
                      className={`h-14 rounded-2xl border text-lg font-medium transition-all ${
                        current === opt ? "border-gold-500 bg-gold-500/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-gold-500/40"
                      }`}>
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {q.type === "choice" && (
                <div className="space-y-3 mb-4" data-testid="answer-choice">
                  {(q.options || []).map((opt) => (
                    <button key={opt} onClick={() => pick(opt)}
                      className={`w-full flex items-center justify-between px-5 h-14 rounded-2xl border text-left transition-all ${
                        current === opt ? "border-gold-500 bg-gold-500/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-gold-500/40"
                      }`}>
                      <span>{opt}</span>
                      {current === opt && <Check className="h-4.5 w-4.5 text-gold-400" />}
                    </button>
                  ))}
                </div>
              )}

              {/* Campo di testo LIBERO disponibile su OGNI domanda: l'utente può scrivere quello che vuole */}
              <Textarea data-testid="answer-input" autoFocus rows={3}
                value={current} onChange={(e) => setCurrent(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) next(); }}
                placeholder={
                  q.type === "yes_no" || q.type === "choice"
                    ? "Scegli sopra oppure scrivi la tua risposta / aggiungi note libere…"
                    : q.unit
                      ? `Scrivi liberamente (es. valore in ${q.unit}, e note aggiuntive)…`
                      : "Scrivi liberamente la tua risposta…"
                }
                className="input-dark text-lg rounded-2xl px-5 py-4 min-h-[96px]" />

              <div className="flex items-center justify-between mt-8">
                <button onClick={prev} disabled={idx === 0} data-testid="audit-prev-btn"
                  className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white disabled:opacity-30 transition-colors">
                  <ArrowLeft className="h-4 w-4" /> Indietro
                </button>
                <Button onClick={next} data-testid="audit-next-btn"
                  className="h-12 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold group">
                  {idx === total - 1 ? "Completa l'audit" : "Continua"}
                  <ArrowRight className="h-4.5 w-4.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
